// v191 — License-Compliance dashboard API client (item 15 of
// docs/PRODUCT-ROADMAP.md).
//
// Three endpoints, all under /api/v1/projects/{id}:
//   GET  /licenses          aggregation: license -> count + samples
//   GET  /license-policy    fetch the project's policy + enforce flag
//   PUT  /license-policy    upsert the project's policy + enforce flag

import useSWR from 'swr';
import { parseErrorResponse } from './problem';

export type LicenseAction = 'allow' | 'warn' | 'block';

export interface LicenseBucket {
  license: string;
  count: number;
  sample_paths: string[];
}

export interface LicensesResponse {
  total: number;
  unknown: number;
  licenses: LicenseBucket[];
}

export interface LicensePolicyResponse {
  policy: Record<string, LicenseAction>;
  enforce: boolean;
  updated_at?: string;
  updated_by_email?: string;
}

const json = async (url: string) => {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) throw await parseErrorResponse(r);
  return r.json();
};

export function useLicenseAggregation(projectId?: string) {
  return useSWR<LicensesResponse>(
    projectId ? `/api/v1/projects/${encodeURIComponent(projectId)}/licenses` : null,
    json,
  );
}

export function useLicensePolicy(projectId?: string) {
  return useSWR<LicensePolicyResponse>(
    projectId
      ? `/api/v1/projects/${encodeURIComponent(projectId)}/license-policy`
      : null,
    json,
  );
}

export async function putLicensePolicy(
  projectId: string,
  policy: Record<string, LicenseAction>,
  enforce: boolean,
): Promise<void> {
  const res = await fetch(
    `/api/v1/projects/${encodeURIComponent(projectId)}/license-policy`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ policy, enforce }),
    },
  );
  if (!res.ok && res.status !== 204) throw await parseErrorResponse(res);
}

// Decide the local effective action for a license string given a
// policy. Mirrors the backend `licensepolicy.Decide` so the UI can
// preview a change before save without an extra roundtrip.
export function decideAction(
  license: string,
  policy: Record<string, LicenseAction>,
): LicenseAction {
  if (license && policy[license]) return policy[license];
  if (policy['default']) return policy['default'];
  return 'allow';
}
